import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import DialogoNovoCanal from '@/components/configuracoes/DialogoNovoCanal';
import DialogoQrCanal from '@/components/configuracoes/DialogoQrCanal';
import {
  atualizarNumero,
  ouvirNumeros,
  papeisDisponiveis,
  statusCanal,
} from '@/services/canaisService';
import { papelDoNumeroPorId, statusConexaoPorId } from '@/lib/constants';

/**
 * O número cria lead novo? Espelha `podeCriarLeadNovo` do servidor
 * (`functions/messaging/numeros.js`): flag booleana no documento sobrepõe o
 * padrão do papel, e o padrão só é "sim" na entrada de marketing.
 */
const criaLeadEfetivo = (n) => {
  if (n.criaLead === true) return true;
  if (n.criaLead === false) return false;
  return n.papel === 'marketing_entrada';
};

/** Só papéis de VENDAS têm o ajuste: na recepção a pergunta não faz sentido. */
const temAjusteDeLead = (n) => n.papel === 'marketing_entrada' || n.papel === 'marketing_resgate';
import { formatarTelefone } from '@/lib/telefone';
import { tratarErro } from '@/lib/erros';

/**
 * Canais de WhatsApp da clínica (PLANO 5.1 e 5.3).
 *
 * Substitui o que até aqui exigia terminal: cadastrar o número no Firestore,
 * criar a instância na Evolution, ler o QR pelo painel da VPS e virar o
 * provider à mão. Agora é uma lista com dois botões.
 *
 * A lista vem do mesmo `ouvirNumeros` que a Central de Conversas usa — listener
 * do Firestore, então cadastrar um canal ou conectar um número reflete aqui
 * sozinho, sem recarregar a página.
 *
 * Ao abrir, a tela RECONCILIA o selo de cada canal com o estado real na
 * Evolution — ver o efeito de reconciliação abaixo.
 */
export default function SecaoCanais() {
  const { clinicaId } = useAuth();
  const [numeros, setNumeros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [canalDoQr, setCanalDoQr] = useState(null);
  const [reconciliando, setReconciliando] = useState(false);

  // Ids já reconciliados nesta montagem. Precisa ser ref, e não estado: mudá-lo
  // não pode provocar render, senão o próprio controle vira o loop que ele
  // existe para evitar.
  const reconciliados = useRef(new Set());
  const [salvandoFlag, setSalvandoFlag] = useState(null);

  /**
   * Liga/desliga a criação de lead novo pelo número.
   *
   * Grava direto no documento, como as mensagens automáticas: é um booleano,
   * não toca a VPS e as rules já restringem `numeros` ao gestor. O servidor
   * relê a flag a cada mensagem recebida — não há cache a invalidar.
   */
  async function alternarCriaLead(numero, ligar) {
    setErro(null);
    setSalvandoFlag(numero.id);
    try {
      await atualizarNumero(clinicaId, numero.id, { criaLead: ligar });
    } catch (e) {
      console.error('[OdontoSDR] Falha ao alterar a criação de lead do canal:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Só o gestor pode alterar se o canal cria lead novo.'
          : 'Não foi possível alterar a criação de lead deste canal.',
      );
    } finally {
      setSalvandoFlag(null);
    }
  }

  useEffect(() => {
    if (!clinicaId) return undefined;
    // Clínica diferente, canais diferentes: o conjunto não pode atravessar.
    reconciliados.current = new Set();
    return ouvirNumeros(
      clinicaId,
      (lista) => {
        // Ordem estável: sem isso a lista dança a cada escrita do servidor.
        setNumeros([...lista].sort((a, b) => (a.papel ?? '').localeCompare(b.papel ?? '')));
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir os canais:', e);
        setErro('Não foi possível carregar os canais desta clínica.');
        setCarregando(false);
      },
    );
  }, [clinicaId]);

  /**
   * Reconcilia o selo com a Evolution, uma vez por canal.
   *
   * O número da recepção foi cadastrado antes de `evolution.statusConexao`
   * existir, então o documento não tem o campo e a tela mostrava
   * "Desconectado" para um WhatsApp que está no ar. Selo que mente é pior que
   * selo ausente: leva o gestor a reconectar um canal que não precisa.
   *
   * `statusCanal` é uma CONSULTA (connectionState) — nunca `conectarCanal`, que
   * emitiria QR e derrubaria a sessão viva da recepção. Ele também grava o
   * `statusConexao` que descobriu, e é o listener acima que traz o valor de
   * volta para a tela.
   *
   * Essa escrita é justamente o risco: cada reconciliação dispara o listener,
   * que reexecuta este efeito. Sem o registro de quem já foi reconciliado,
   * seria um laço infinito chamando a VPS. Por isso o id entra no conjunto
   * ANTES do await — o listener volta enquanto as chamadas ainda estão no ar.
   */
  useEffect(() => {
    if (!clinicaId || numeros.length === 0) return;

    const pendentes = numeros.filter(
      (n) =>
        // A via oficial não tem instância nem pareamento a consultar.
        n.provider !== 'cloud_api' &&
        n.evolution?.instancia &&
        n.evolution?.baseUrl &&
        !reconciliados.current.has(n.id),
    );
    if (pendentes.length === 0) return;

    for (const n of pendentes) reconciliados.current.add(n.id);
    setReconciliando(true);

    (async () => {
      // Em série: são três canais no máximo, e não há motivo para abrir várias
      // conversas simultâneas com uma VPS modesta.
      for (const n of pendentes) {
        try {
          await statusCanal({ clinicaId, numeroId: n.id });
        } catch (e) {
          // Falha em um canal não pode derrubar a lista nem interromper os
          // outros: o pior caso é o selo daquele canal seguir como estava.
          // ★ WT 1b: a decisão fica; entra o registro.
          tratarErro('o status de um canal', null, 'callable')(e);
        }
      }
      // `finally` implícito por construção: o laço não relança, então isto roda
      // mesmo com todos os canais falhando. Botão que ficasse travado seria pior
      // que selo desatualizado.
      setReconciliando(false);
    })();
  }, [clinicaId, numeros]);

  // O canal do modal precisa vir da lista viva, e não de uma cópia congelada no
  // clique: é assim que o `statusConexao` que o servidor grava durante a
  // conexão chega ao modal aberto.
  const canalSelecionado = canalDoQr ? (numeros.find((n) => n.id === canalDoQr) ?? null) : null;

  const podeAdicionar = papeisDisponiveis(numeros).length > 0;

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Canais de WhatsApp</h2>
          <p className="secao-sub">
            Números da clínica e o estado da conexão de cada um.
          </p>
        </div>
        <button
          type="button"
          className="btn-primario compacto"
          onClick={() => setNovoAberto(true)}
          disabled={!podeAdicionar}
          title={podeAdicionar ? undefined : 'Todos os papéis já têm canal cadastrado'}
        >
          Adicionar canal
        </button>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="tabela-envolve">
        <table className="tabela">
          <thead>
            <tr>
              <th>Canal</th>
              <th>Telefone</th>
              <th>Status</th>
              <th>Cria lead novo</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {numeros.map((n) => {
              const papel = papelDoNumeroPorId(n.papel);
              const oficial = n.provider === 'cloud_api';
              const status = statusConexaoPorId(n.evolution?.statusConexao);
              const conectado = n.evolution?.statusConexao === 'conectado';

              return (
                <tr key={n.id}>
                  <td>
                    <strong>{papel.label}</strong>
                    {n.evolution?.instancia && (
                      <span className="canal-instancia">{n.evolution.instancia}</span>
                    )}
                  </td>
                  <td>{n.telefone ? formatarTelefone(n.telefone) : '—'}</td>
                  <td>
                    {oficial ? (
                      // A via oficial conecta por cadastro na Meta, não por QR:
                      // mostrar "Desconectado" aqui seria mentira.
                      <span className="pilula pequena" style={{ background: '#0ea5e9' }}>
                        API oficial (Meta)
                      </span>
                    ) : (
                      <span className="pilula pequena" style={{ background: status.cor }}>
                        {status.label}
                      </span>
                    )}
                  </td>
                  <td>
                    {temAjusteDeLead(n) ? (
                      <label
                        className="canal-cria-lead"
                        title={
                          n.papel === 'marketing_resgate'
                            ? 'Ligue enquanto o número oficial não estiver conectado. Mensagem de telefone desconhecido neste número passa a criar lead (a origem vem da primeira mensagem, se a inferência estiver ligada em Captação).'
                            : 'Desligue para o número de entrada parar de criar lead novo.'
                        }
                      >
                        <input
                          type="checkbox"
                          checked={criaLeadEfetivo(n)}
                          disabled={salvandoFlag === n.id}
                          onChange={(e) => alternarCriaLead(n, e.target.checked)}
                          aria-label={`${papel.label} cria lead novo`}
                        />
                        <span>{criaLeadEfetivo(n) ? 'Sim' : 'Não'}</span>
                      </label>
                    ) : (
                      <span className="canal-sem-acao">—</span>
                    )}
                  </td>
                  <td className="celula-acoes">
                    {oficial ? (
                      <span className="canal-sem-acao">Conecta pelo painel da Meta</span>
                    ) : (
                      <button
                        type="button"
                        className={conectado ? 'btn-secundario compacto' : 'btn-primario compacto'}
                        onClick={() => setCanalDoQr(n.id)}
                        // Enquanto a reconciliação não volta, o rótulo ainda vem
                        // do campo que pode estar desatualizado. Clicar aqui num
                        // canal que está no ar, mas ainda marcado como
                        // desconectado, pularia o aviso de reconexão e emitiria
                        // QR no número vivo. São ~1s — barato esperar.
                        disabled={reconciliando}
                      >
                        {reconciliando ? 'Verificando…' : conectado ? 'Reconectar' : 'Conectar'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {carregando && <p className="vazio">Carregando os canais…</p>}
        {!carregando && numeros.length === 0 && (
          <p className="vazio">
            Nenhum canal cadastrado. Use o botão “Adicionar canal” para cadastrar o primeiro.
          </p>
        )}
      </div>

      <DialogoNovoCanal
        aberto={novoAberto}
        aoFechar={() => setNovoAberto(false)}
        clinicaId={clinicaId}
        numeros={numeros}
      />

      <DialogoQrCanal
        aberto={Boolean(canalSelecionado)}
        aoFechar={() => setCanalDoQr(null)}
        clinicaId={clinicaId}
        canal={canalSelecionado}
      />
    </section>
  );
}
